import React, { useState } from "react"
import { registerLocale } from "react-datepicker"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import ptBR from "date-fns/locale/pt-BR"

import { Button, DateTime, Input } from "../../components/DesignSystem"

import Header from "./components/Header"

import { useSnackbar } from "../../hooks/useSnackbar"

import {
  cnpjAlphanumeric as cnpjMask,
  maskCurrency,
  validateCnpjAlphanumeric as validateCnpj,
  floatToCurrency,
} from "../../constants/validate"

import {
  controlInputCurrency,
  controlCnpjAlphanumericInput as controlCnpjInput,
} from "../../constants/controlInput"

import "react-datepicker/dist/react-datepicker.css"

import "./index.scss"

function CaseForm() {
  const [negotiationId, setNegotiationId] = useState("")
  const [customerNumber, setCustomerNumber] = useState("")
  const [cnpj, setCnpj] = useState("")
  const [documentType, setDocumentType] = useState("")
  const [documentNumber, setDocumentNumber] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [assignment, setAssignment] = useState("")
  const [releaseDate, setReleaseDate] = useState("")
  const [releaseDateHasError, setReleaseDateHasError] = useState(false)
  const [dueDate, setDueDate] = useState("")
  const [dueDateHasError, setDueDateHasError] = useState("")
  const [baseSum, setBaseSum] = useState("")

  const [isSubmiting, setIsSubmiting] = useState(false)
  const snackbar = useSnackbar()
  const navigate = useNavigate()

  registerLocale("ptBR", ptBR)

  function handleBlur(type, value) {
    switch (type) {
      case "cnpj":
        return validateCnpj(value)
      default:
        return value !== ""
    }
  }

  function handleChangeCnpj(value, evt) {
    controlCnpjInput(cnpj, value, evt)

    setCnpj(removeMask(value))
  }

  function handleChangeBaseSum(value, evt) {
    let newValue = value.replace(/\D/g, "")
    newValue = (Number(newValue) / 100).toFixed(2)

    controlInputCurrency(baseSum, value, evt)

    setBaseSum(Number(newValue))
  }

  function handleChangeDueDate(value) {
    setDueDate(value)
  }

  function handleChangeReleaseDate(value) {
    setReleaseDate(value)
  }

  function resetForm() {
    setCompanyName("")
    setCnpj("")
    setReleaseDate("")
    setNegotiationId("")
    setCustomerNumber("")
    setDocumentNumber("")
    setDocumentType("")
    setReleaseDate("")
    setDueDate("")
    setAssignment("")
    setBaseSum("")
    setReleaseDateHasError(false)
    setDueDateHasError(false)
  }

  function handleGoCase(caseId) {
    navigate("/painel/meus-casos", {
      state: { id: caseId },
    })
  }

  async function handleSubmit(event) {
    event.preventDefault()

    const newCase = {
      negotiationId,
      customerNumber,
      cnpj,
      documentNumber,
      documentType,
      companyName,
      assignment,
      releaseDate,
      dueDate,
      baseSum,
    }

    setIsSubmiting(true)
    await axios
      .post("/case/add", newCase)
      .then((response) => {
        if (response.data.status !== 401) {
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
          resetForm()
          snackbar("Caso inserido com sucesso", {
            handleCallback: () => handleGoCase(response.data.id),
            showCallback: true,
            callbackMessage: "Exibir caso",
          })
        }
      })
      .catch(() => {
        snackbar("Houve um erro ao inserir o caso")
      })
      .finally(() => {
        setIsSubmiting(false)
      })
  }

  function removeMask(value) {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, "")
  }

  return (
    <div className="case-form">
      <div className="case-form__inner">
        <Header />
        <form onSubmit={handleSubmit}>
          <section className="case-form__company">
            <Input
              value={negotiationId}
              label="ID da negociação"
              placeholder="ID da negociação"
              type="text"
              required
              onChange={(value) => setNegotiationId(value)}
              onBlur={(value) => handleBlur("default", value)}
            />
            <Input
              value={customerNumber}
              label="Número do cliente"
              placeholder="Número do cliente"
              type="text"
              required
              onChange={(value) => setCustomerNumber(value)}
              onBlur={(value) => handleBlur("default", value)}
            />
            <Input
              value={cnpjMask(cnpj)}
              label="CNPJ"
              placeholder="CNPJ"
              type="text"
              required
              onBlur={(value) => handleBlur("cnpj", value)}
              onChange={(value, evt) => handleChangeCnpj(value, evt)}
            />
            <Input
              value={documentType}
              label="Número do documento"
              placeholder="Número do documento"
              type="text"
              required
              onChange={(value) => setDocumentType(value)}
              onBlur={(value) => handleBlur("default", value)}
            />
            <Input
              value={documentNumber}
              label="Número do documento"
              placeholder="Número do documento"
              type="text"
              required
              onChange={(value) => setDocumentNumber(value)}
              onBlur={(value) => handleBlur("default", value)}
            />
            <Input
              value={companyName}
              label="Empresa"
              placeholder="Empresa"
              type="text"
              required
              onChange={(value) => setCompanyName(value)}
              onBlur={(value) => handleBlur("default", value)}
            />
            <Input
              value={assignment}
              label="Exercício"
              placeholder="Exercício"
              type="text"
              required
              onChange={(value) => setAssignment(value)}
              onBlur={(value) => handleBlur("default", value)}
            />
            <DateTime
              value={releaseDate}
              placeholder="Data de lançamento"
              label="Data de lançamento"
              onChange={handleChangeReleaseDate}
              onBlur={(value) => handleBlur("default", value)}
              error={releaseDateHasError}
              required={true}
            />
            <DateTime
              value={dueDate}
              placeholder="Data de vencimento"
              label="Data de vencimento"
              onChange={handleChangeDueDate}
              onBlur={(value) => handleBlur("default", value)}
              error={dueDateHasError}
              required={true}
            />
            <Input
              value={maskCurrency(floatToCurrency(baseSum), true)}
              label="Montante base"
              placeholder="Montante base"
              type="text"
              required
              onChange={(value, evt) => handleChangeBaseSum(value, evt)}
            />
          </section>

          <Button
            type="submit"
            contained
            fullWidth
            loading={isSubmiting}
            disabled={isSubmiting}
          >
            Inserir caso
          </Button>
        </form>
      </div>
    </div>
  )
}

export default CaseForm
